import React, { useEffect, useState } from 'react';
import { useIonToast } from '@ionic/react';
import { Input } from '../../components/Forms/Input.js';
import { Header } from '../../components/Header.js';
import { IconButton } from '../../components/IconButton.js';
import { IconArrowBack, IconUpload } from '../../components/QuirkIcons/index.js';
import { QuirkIonContent } from '../../components/QuirkIonContent.js';
import { QuirkIonPage } from '../../components/QuirkIonPage.js';
import { useRSSImportMutation } from '../../api/ImportAPI.js';
import { RSSImportParams, User } from 'model';
import { TopicSearchBox } from '../../components/Forms/TopicSearchBox.js';
import { SelectUser } from '../../components/SelectUser.js';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from '../../components/Forms/FormFields.module.css';
import styles from './ImportRSSFeed.module.css';
import { QuirkLoading } from '../../components/QuirkLoading.js';
import { useHistory } from 'react-router';

const pageTitle = 'RSS Feed Import';

function isValidURL(url: string | undefined): boolean {
	if (!url) {
		return false;
	}
	try {
		const urlObject = new URL(url);
		return urlObject !== null;
	} catch (e) {
		return false;
	}
}

type ImportRSSFeedState = Omit<RSSImportParams, 'author'> & { author: User | undefined };

export function ImportRSSFeedPage() {
	const history = useHistory();
	const [state, setState] = useState<ImportRSSFeedState>({
		feed: '',
		topic: '',
		author: undefined,
		maxItems: 50
	});
	const onFeedURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setState(current => ({ ...current, feed: event.target.value }));
	};
	const onFeedImport = () => {
		if (state.author) {
			importRSSFeed({
				...state,
				author: state.author?.username
			});
		}
	};
	const [importRSSFeed, { isLoading, isError, isSuccess, data, error, reset }] = useRSSImportMutation();
	const validate = ({ feed, topic, author, maxItems }: ImportRSSFeedState) => {
		return isValidURL(feed) && topic && author && maxItems;
	};
	const [toast] = useIonToast();
	useEffect(() => {
		if (isSuccess) {
			const duplicates = data?.errors.filter(({ errorCode }) => errorCode === 409).length ?? 0;
			const errors = data?.errors.filter(({ errorCode }) => errorCode !== 409).length ?? 0;
			let message = `Imported ${data?.posts.length} posts, with ${duplicates} duplicates ignored`;
			if (errors) {
				message += ` and ${errors} failures`;
			}
			toast({
				color: 'success',
				position: 'top',
				message,
				duration: 3000
			});
			reset();
			setState({
				feed: '',
				topic: '',
				author: undefined,
				maxItems: 50
			});
		}
	}, [isSuccess, data, toast, reset]);
	useEffect(() => {
		if (isError) {
			toast({
				message: (error as any).data,
				duration: 1500,
				color: 'danger',
				position: 'top'
			});
		}
	}, [isError, error, toast]);

	return (
		<QuirkIonPage name={pageTitle}>
			<Header title={pageTitle} left={<IconButton icon={<IconArrowBack />} onClick={() => history.goBack()} />} />
			<QuirkIonContent>
				<>
					<QuirkLoading isLoading={isLoading} message="Importing RSS Feed" />
					<div className={styles.feedURLInput}>
						<div>
							<div className={formStyles.label}>Feed URL</div>
							<Input
								name="feedURL"
								value={state.feed}
								onChange={onFeedURLChange}
								placeholder="Feed URL"
							/>
						</div>
						<TopicSearchBox
							label="Default Topic"
							topic={state.topic}
							setValues={({ topic }: { topic?: string }) => {
								setState(current => ({ ...current, topic: topic ?? '' }));
							}}
							testId="selectImportTopic"
						/>
						<SelectUser
							label="Default Author"
							onChange={(user?: User) => {
								setState(current => ({ ...current, author: user }));
							}}
							value={state.author}
						/>
						<Input type="number" value={state.maxItems} label={'Max Items'} />
						<IconButton
							icon={<IconUpload />}
							label="Import"
							iconRight
							disabled={!validate(state) || isLoading}
							onClick={onFeedImport}
						/>
					</div>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
}
